import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    const next = params.get("next");
    if (next) navigate(next);
    else if (res.user.role === "admin") navigate("/admin");
    else if (res.user.role === "staff") navigate("/staff");
    else navigate("/app");
  }

  return (
    <AuthShell title="Willkommen zurück" subtitle="Melde dich an, um fortzufahren.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <Field label="E-Mail" type="email" value={email} onChange={setEmail} testid="login-email-input" autoComplete="email" required />
        <Field label="Passwort" type="password" value={password} onChange={setPassword} testid="login-password-input" autoComplete="current-password" required />
        {error && <div className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={loading} data-testid="login-submit-button"
          className="w-full mt-2 inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold hover:bg-[#FAD02C] transition-colors disabled:opacity-60 gold-glow">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Anmelden <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <div className="mt-6 text-sm text-[#A1A1AA] text-center">
        Noch kein Konto? <Link to="/register" className="text-[#EBB300] hover:underline" data-testid="login-to-register-link">Registrieren</Link>
      </div>

      <div className="mt-8 p-4 bg-[#0d0d11] border border-white/5 rounded-xl text-xs text-[#A1A1AA] space-y-1" data-testid="login-demo-hint">
        <div className="font-semibold text-[#EBB300] tracking-[0.15em] uppercase text-[10px]">Demo-Logins</div>
        <div>Admin: <span className="text-white">admin@nod.app</span> / admin123</div>
        <div>Staff: <span className="text-white">lena@nod.app</span> / staff123</div>
        <div>Gast: <span className="text-white">gast@nod.app</span> / gast123</div>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6 nod-grain">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-baseline gap-2 mb-10 justify-center" data-testid="auth-logo">
          <span className="text-3xl font-black tracking-tight text-white">Nod</span>
          <span className="w-2 h-2 rounded-full bg-[#EBB300]"></span>
        </Link>
        <div className="bg-[#121216] border border-white/5 rounded-2xl p-8">
          <h1 className="text-3xl font-black tracking-tight mb-2 text-white" data-testid="auth-title">{title}</h1>
          <p className="text-[#A1A1AA] mb-6">{subtitle}</p>
          {children}
        </div>
        <Link to="/" className="block text-center text-xs text-[#52525B] mt-6 hover:text-white" data-testid="auth-back-home">← Zurück zur Startseite</Link>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase text-[#A1A1AA] mb-2 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#52525B] focus:border-[#EBB300] transition-colors"
        {...rest}
      />
    </label>
  );
}
